import { useEffect, useMemo, useState } from 'react';

import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { searchGeneral } from '../../services/algolia';
import { useResolvedHitImage } from '../../hooks/useResolvedHitImage';
import type { WatchReferenceAlgoliaHit } from '../../types';
import type { ManualReference } from './collectionPayload';

/**
 * Search-and-add for a collection's manual reference list.
 *
 * A modal rather than the inline dropdown it replaces, for two reasons a
 * curator hit every time they built a landing page: the dropdown stayed open
 * after a pick (it was driven by the query alone, so choosing a watch left the
 * list hanging over the fields below), and a row carried only a reference
 * number, which is not enough to tell two dial variants apart. Here a pick
 * ticks a checkbox, the list stays put until the curator is done, and every row
 * shows the watch.
 */

const SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const RESULT_LIMIT = 20;

interface ReferencePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Appended to the collection, in the order they were ticked. */
  onAdd: (added: ManualReference[]) => void;
  /** Ids already on the collection — shown ticked and locked. */
  existingIds: Set<string>;
  /** Brand id → display name, so a row can say "Rolex" not "rolex". */
  brandNames: Map<string, string>;
  /** Options for the brand narrow, already sorted by name. */
  brandOptions: { id: string; name: string }[];
  /** Remaining slots, when the collection caps its list. Undefined means no cap. */
  remaining?: number;
}

export function hitToManualReference(
  hit: WatchReferenceAlgoliaHit,
): ManualReference {
  return {
    id: hit.objectID,
    reference: hit.reference,
    brandId: hit.brandId,
    model: hit.model,
  };
}

export function ReferencePickerModal({
  isOpen,
  onClose,
  onAdd,
  existingIds,
  brandNames,
  brandOptions,
  remaining,
}: ReferencePickerModalProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [brandId, setBrandId] = useState('');
  const [hits, setHits] = useState<WatchReferenceAlgoliaHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Keyed by id and held across searches: a curator picks a Speedmaster, then
   * searches "Submariner", and both are still going to be added. A Map also
   * keeps insertion order, which is the order the landing carousel renders.
   */
  const [staged, setStaged] = useState<Map<string, ManualReference>>(new Map());

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setDebouncedQuery('');
    setBrandId('');
    setHits([]);
    setError(null);
    setStaged(new Map());
  }, [isOpen]);

  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedQuery(query),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    if (!isOpen) return;
    const q = debouncedQuery.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setHits([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    setError(null);
    searchGeneral({
      query: q,
      limit: RESULT_LIMIT,
      ...(brandId ? { brandId } : {}),
    })
      .then((res) => {
        if (!cancelled) setHits(res.hits);
      })
      .catch((err) => {
        console.error('Reference search failed:', err);
        if (!cancelled) {
          setHits([]);
          setError('Search is unavailable right now.');
        }
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, debouncedQuery, brandId]);

  const stagedCount = staged.size;
  const atCap = remaining !== undefined && stagedCount >= remaining;

  const toggle = (hit: WatchReferenceAlgoliaHit) => {
    if (existingIds.has(hit.objectID)) return;
    setStaged((prev) => {
      const next = new Map(prev);
      if (next.has(hit.objectID)) {
        next.delete(hit.objectID);
      } else {
        if (remaining !== undefined && next.size >= remaining) return prev;
        next.set(hit.objectID, hitToManualReference(hit));
      }
      return next;
    });
  };

  const handleAdd = () => {
    if (stagedCount === 0) return;
    onAdd(Array.from(staged.values()));
    onClose();
  };

  const needsMoreTyping = query.trim().length < MIN_QUERY_LENGTH;

  const addLabel = useMemo(
    () =>
      stagedCount === 0
        ? 'Add references'
        : `Add ${stagedCount} reference${stagedCount === 1 ? '' : 's'}`,
    [stagedCount],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add references" size="xl">
      <div className="space-y-4">
        <div className="flex gap-3 flex-wrap">
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by reference number, model, or collection…"
            aria-label="Search references"
            className="flex-1 min-w-[240px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <select
            value={brandId}
            onChange={(e) => setBrandId(e.target.value)}
            aria-label="Narrow by brand"
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">All brands</option>
            {brandOptions.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {atCap && (
          <p className="text-xs text-amber-600">
            That is every remaining slot on this collection. Add these, or untick
            one to swap it.
          </p>
        )}

        <div className="border border-gray-200 rounded-lg divide-y max-h-[26rem] overflow-y-auto">
          {needsMoreTyping && (
            <p className="px-3 py-8 text-sm text-gray-500 text-center">
              Type at least {MIN_QUERY_LENGTH} characters to search.
            </p>
          )}
          {!needsMoreTyping && searching && hits.length === 0 && (
            <p className="px-3 py-8 text-sm text-gray-500 text-center">
              Searching…
            </p>
          )}
          {!needsMoreTyping && !searching && hits.length === 0 && !error && (
            <p className="px-3 py-8 text-sm text-gray-500 text-center">
              No matches.
            </p>
          )}
          {hits.map((hit) => (
            <ReferenceRow
              key={hit.objectID}
              hit={hit}
              brandName={brandNames.get(hit.brandId) || hit.brandId}
              already={existingIds.has(hit.objectID)}
              picked={staged.has(hit.objectID)}
              disabled={atCap && !staged.has(hit.objectID)}
              onToggle={() => toggle(hit)}
            />
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={stagedCount === 0}>
            {addLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

interface ReferenceRowProps {
  hit: WatchReferenceAlgoliaHit;
  brandName: string;
  already: boolean;
  picked: boolean;
  disabled: boolean;
  onToggle: () => void;
}

function ReferenceRow({
  hit,
  brandName,
  already,
  picked,
  disabled,
  onToggle,
}: ReferenceRowProps) {
  const imageUrl = useResolvedHitImage(hit.imageUrl);
  const locked = already || disabled;

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={locked}
      aria-pressed={picked || already}
      className={`w-full text-left px-3 py-2 flex items-center gap-3 ${
        locked ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'
      } ${picked ? 'bg-brand-50' : ''}`}
    >
      <input
        type="checkbox"
        checked={picked || already}
        readOnly
        disabled={locked}
        tabIndex={-1}
        aria-hidden="true"
        className="shrink-0"
      />
      <ReferenceThumbnail url={imageUrl} alt={hit.reference} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 truncate">
          {hit.reference}
          {hit.model && (
            <span className="font-normal text-gray-600"> — {hit.model}</span>
          )}
        </div>
        <div className="text-xs text-gray-500 truncate">
          {[brandName, hit.collection].filter(Boolean).join(' · ')}
        </div>
      </div>
      {already && (
        <span className="text-xs text-gray-400 shrink-0">Added</span>
      )}
    </button>
  );
}

interface ReferenceThumbnailProps {
  url: string | null;
  alt: string;
  /** Tailwind size classes, so the selected list can render a smaller tile. */
  className?: string;
}

/**
 * The picked watch, or a placeholder tile. A reference can be indexed without a
 * picture and a signed URL can still 404, so the fallback covers both — an
 * empty box would read as a broken row rather than a watch with no photo.
 */
export function ReferenceThumbnail({
  url,
  alt,
  className = 'w-14 h-14',
}: ReferenceThumbnailProps) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [url]);

  if (!url || failed) {
    return (
      <div
        className={`${className} shrink-0 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-400 text-center leading-tight`}
      >
        No image
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={alt}
      onError={() => setFailed(true)}
      className={`${className} shrink-0 rounded object-cover bg-gray-100 border border-gray-200`}
    />
  );
}
